import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import RichTextEditor from '@/components/ui/rich-text-editor';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Link, Award, Image as ImageIcon, X, Plus, Loader2, Building2, Images } from 'lucide-react';
import { useCreateArticleAuthor, useUpdateArticleAuthor } from '@/hooks/useArticleAuthors';
import { ArticleMediaLibrary } from '@/components/articles/ArticleMediaLibrary';
import type { ArticleAuthor, SocialProofItem, AuthorImage } from '@/types/articles';

interface ArticleAuthorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  author: ArticleAuthor | null;
  brandId: string;
}

export function ArticleAuthorDialog({
  open,
  onOpenChange,
  author,
  brandId,
}: ArticleAuthorDialogProps) {
  const createAuthor = useCreateArticleAuthor();
  const updateAuthor = useUpdateArticleAuthor();
  const isEditing = !!author;

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    title: '',
    bio: '',
    email: '',
    avatar_url: '',
    linkedin: '',
    twitter: '',
    website: '',
    expertise_areas: [] as string[],
    is_expert: false,
    social_proof: [] as SocialProofItem[],
    images: [] as AuthorImage[],
  });
  const [newExpertise, setNewExpertise] = useState('');
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [mediaLibraryTarget, setMediaLibraryTarget] = useState<'avatar' | 'social_proof' | 'images'>('avatar');
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (author) {
      setFormData({
        name: author.name,
        slug: author.slug,
        title: author.title || '',
        bio: author.bio || '',
        email: author.email || '',
        avatar_url: author.avatar_url || '',
        linkedin: author.social_links?.linkedin || '',
        twitter: author.social_links?.twitter || '',
        website: author.social_links?.website || '',
        expertise_areas: author.expertise_areas || [],
        is_expert: author.is_expert,
        social_proof: (author.social_proof as SocialProofItem[]) || [],
        images: (author.images as AuthorImage[]) || [],
      });
    } else {
      setFormData({
        name: '',
        slug: '',
        title: '',
        bio: '',
        email: '',
        avatar_url: '',
        linkedin: '',
        twitter: '',
        website: '',
        expertise_areas: [],
        is_expert: false,
        social_proof: [],
        images: [],
      });
    }
    setActiveTab('basic');
  }, [author, open]);

  // Auto-generate slug from name
  useEffect(() => {
    if (!isEditing && formData.name && !formData.slug) {
      setFormData(prev => ({
        ...prev,
        slug: formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
      }));
    }
  }, [formData.name, isEditing]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;

    const socialLinks = {
      linkedin: formData.linkedin || undefined,
      twitter: formData.twitter || undefined,
      website: formData.website || undefined,
    };

    const authorData = {
      name: formData.name,
      slug: formData.slug,
      title: formData.title || null,
      bio: formData.bio || null,
      email: formData.email || null,
      avatar_url: formData.avatar_url || null,
      social_links: Object.values(socialLinks).some(v => v) ? socialLinks : null,
      expertise_areas: formData.expertise_areas,
      is_expert: formData.is_expert,
      social_proof: formData.social_proof,
      images: formData.images,
    };

    if (isEditing) {
      await updateAuthor.mutateAsync({ id: author.id, ...authorData });
    } else {
      await createAuthor.mutateAsync({ brand_id: brandId, ...authorData });
    }

    onOpenChange(false);
  };

  const addExpertise = () => {
    if (newExpertise.trim() && !formData.expertise_areas.includes(newExpertise.trim())) {
      setFormData(prev => ({
        ...prev,
        expertise_areas: [...prev.expertise_areas, newExpertise.trim()]
      }));
      setNewExpertise('');
    }
  };

  const removeExpertise = (area: string) => {
    setFormData(prev => ({
      ...prev,
      expertise_areas: prev.expertise_areas.filter(e => e !== area)
    }));
  };

  const addSocialProof = (url: string) => {
    setFormData(prev => ({
      ...prev,
      social_proof: [...prev.social_proof, { url, alt: '' }]
    }));
  };

  const updateSocialProof = (index: number, field: keyof SocialProofItem, value: string) => {
    setFormData(prev => ({
      ...prev,
      social_proof: prev.social_proof.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeSocialProof = (index: number) => {
    setFormData(prev => ({
      ...prev,
      social_proof: prev.social_proof.filter((_, i) => i !== index)
    }));
  };

  const addImage = (url: string) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, { url }]
    }));
  };

  const updateImage = (index: number, field: keyof AuthorImage, value: string) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const openMediaLibrary = (target: 'avatar' | 'social_proof' | 'images') => {
    setMediaLibraryTarget(target);
    setMediaLibraryOpen(true);
  };

  const handleMediaSelect = (url: string) => {
    if (mediaLibraryTarget === 'avatar') {
      setFormData(prev => ({ ...prev, avatar_url: url }));
    } else if (mediaLibraryTarget === 'social_proof') {
      addSocialProof(url);
    } else if (mediaLibraryTarget === 'images') {
      addImage(url);
    }
    setMediaLibraryOpen(false);
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isSaving = createAuthor.isPending || updateAuthor.isPending;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitleWithBug
              popupName={isEditing ? 'Edit Author' : 'Add Author'}
              popupContext="/articles/authors"
              isOpen={open}
            >
              {isEditing ? 'Edit Author' : 'Add Author'}
            </DialogTitleWithBug>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
              <TabsTrigger value="basic" className="gap-2">
                <User className="h-3.5 w-3.5" />
                Basic
              </TabsTrigger>
              <TabsTrigger value="social" className="gap-2">
                <Link className="h-3.5 w-3.5" />
                Links
              </TabsTrigger>
              <TabsTrigger value="expertise" className="gap-2">
                <Award className="h-3.5 w-3.5" />
                Expertise
              </TabsTrigger>
              <TabsTrigger value="social_proof" className="gap-2">
                <Building2 className="h-3.5 w-3.5" />
                Social Proof
              </TabsTrigger>
              <TabsTrigger value="images" className="gap-2">
                <Images className="h-3.5 w-3.5" />
                Images
              </TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              {/* Avatar */}
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={formData.avatar_url || undefined} />
                  <AvatarFallback className="text-lg">
                    {formData.name ? getInitials(formData.name) : <User className="h-8 w-8" />}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openMediaLibrary('avatar')}
                  >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    {formData.avatar_url ? 'Change Avatar' : 'Upload Avatar'}
                  </Button>
                  {formData.avatar_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, avatar_url: '' }))}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') 
                    }))}
                    placeholder="john-doe"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Growth Specialist"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Bio</Label>
                <RichTextEditor
                  value={formData.bio}
                  onChange={(value) => setFormData(prev => ({ ...prev, bio: value }))}
                  placeholder="A brief biography..."
                />
              </div>
            </TabsContent>

            <TabsContent value="social" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>LinkedIn</Label>
                <Input
                  value={formData.linkedin}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkedin: e.target.value }))}
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
              <div className="space-y-2">
                <Label>Twitter / X</Label>
                <Input
                  value={formData.twitter}
                  onChange={(e) => setFormData(prev => ({ ...prev, twitter: e.target.value }))}
                  placeholder="https://twitter.com/username"
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>
            </TabsContent>

            <TabsContent value="expertise" className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <Label>Is Expert</Label>
                <Switch
                  checked={formData.is_expert}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_expert: checked }))}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Mark as expert to allow selection as an expert reviewer for articles.
              </p>

              <div className="space-y-2">
                <Label>Expertise Areas</Label>
                <div className="flex gap-2">
                  <Input
                    value={newExpertise}
                    onChange={(e) => setNewExpertise(e.target.value)}
                    placeholder="e.g., SEO, Growth Hacking"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExpertise())}
                  />
                  <Button type="button" variant="outline" onClick={addExpertise}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {formData.expertise_areas.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.expertise_areas.map((area) => (
                      <Badge key={area} variant="secondary" className="gap-1">
                        {area}
                        <X
                          className="h-3 w-3 cursor-pointer hover:text-destructive"
                          onClick={() => removeExpertise(area)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="social_proof" className="space-y-4 mt-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Social Proof Logos</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openMediaLibrary('social_proof')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Logo
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Add logos of companies, publications, or credentials to showcase credibility.
                </p>
              </div>

              {formData.social_proof.length > 0 ? (
                <div className="space-y-3">
                  {formData.social_proof.map((item, index) => (
                    <div key={index} className="flex gap-3 items-start p-3 border rounded-lg">
                      <img
                        src={item.url}
                        alt={item.alt || 'Logo'}
                        className="w-16 h-16 object-contain bg-muted rounded"
                      />
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Alt text (e.g., Forbes)"
                          value={item.alt}
                          onChange={(e) => updateSocialProof(index, 'alt', e.target.value)}
                        />
                        <Input
                          placeholder="Link URL (optional)"
                          value={item.link || ''}
                          onChange={(e) => updateSocialProof(index, 'link', e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeSocialProof(index)}
                      >
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border rounded-lg border-dashed">
                  <Building2 className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No social proof logos added yet</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="images" className="space-y-4 mt-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Author Images</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openMediaLibrary('images')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Image
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Add additional images for the author (headshots, action shots, etc.)
                </p>
              </div>

              {formData.images.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {formData.images.map((item, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={item.url}
                        alt={item.alt || 'Author image'}
                        className="w-full aspect-square object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col justify-end p-2">
                        <Input
                          placeholder="Alt text"
                          value={item.alt || ''}
                          onChange={(e) => updateImage(index, 'alt', e.target.value)}
                          className="mb-1 text-xs h-8"
                        />
                        <Input
                          placeholder="Type (e.g., headshot)"
                          value={item.type || ''}
                          onChange={(e) => updateImage(index, 'type', e.target.value)}
                          className="text-xs h-8"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeImage(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border rounded-lg border-dashed">
                  <Images className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No additional images added yet</p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving || !formData.name.trim()}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Create Author'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ArticleMediaLibrary
        open={mediaLibraryOpen}
        onOpenChange={setMediaLibraryOpen}
        onSelect={handleMediaSelect}
      />
    </>
  );
}
